// src/core/financing.ts
// Einheitliche Darlehensregel (Dominiks Vorgabe, 16.10.2026) -- EINE Funktion fuer alle 5
// Objektarten (MFH/EFH/Gewerbe/Gemischt/ETW), den Finanzierungs-Check/Optimierer
// (dealFinancingEvaluator.ts) und den /check-Generic-Adapter
// (quickCheckGenericAdapter.ts). Ersetzt die bisher parallel existierenden drei Varianten:
// MFHs eigene Inline-Rechnung (core/metrics/mfh.ts), die alte "Kaufpreis x LTV"/"All-in x
// LTV"-Formel der anderen 4 Typen, und eigenkapitalToFinancing()s "All-in minus EK"-Naeherung
// (quickCheckConversions.ts, bisher vom Optimierer UND von ETWs /check-Pfad genutzt).
//
// Eigenkapital in Euro ist der universelle Eingabewert, Beleihungsquote (LTV) nur noch eine
// ABGELEITETE Anzeige-Groesse (nie mehr ein Eingabefeld).
//
// Regel: das Eigenkapital deckt zuerst die Nebenkosten, der Kredit den Rest des Kaufpreises
// (Kredit = Kaufpreis − max(0, EK − Nebenkosten)). Reicht das EK nicht fuer die Nebenkosten,
// bleibt eine Luecke (Luecke = max(0, Nebenkosten − EK)), statt stillschweigend einen zu
// grossen Kredit anzunehmen. `nebenkosten` ist bewusst ein Parameter (nicht intern aus
// `kaufpreis` hergeleitet) -- Break-even-/Zielsuche-Bisektionen, die den Kaufpreis selbst
// variieren, muessen die Nebenkosten je Iteration SELBST neu berechnen (sie skalieren meist
// mit dem Kaufpreis) und hier einfach erneut hereinreichen; diese Funktion bleibt dadurch frei
// von jeder Kaufnebenkosten-Berechnungslogik.
export interface FinancingInput {
  kaufpreis: number;
  nebenkosten: number;
  eigenkapital: number;
  /** Default true. MFH hat historisch keinen eigenen financingOn-Schalter (Barkauf ergibt
   *  sich dort bereits rechnerisch aus einem sehr hohen Eigenkapital) -- fuer MFH immer
   *  `true` uebergeben. */
  financingOn?: boolean;
  /** Bisher nur MFH: wenn gesetzt, wird `darlehenManual` direkt als Kredit uebernommen
   *  (bei < 0 auf 0 gekappt). `eigenkapital` bleibt trotzdem der Wert, der in die
   *  Luecke-Berechnung einfliesst. */
  manualLoan?: boolean;
  darlehenManual?: number;
}

export interface FinancingResult {
  loan: number;
  /** Der Eingabewert (nach `Math.max(0, ...)`), nicht neu hergeleitet -- identisch zu
   *  `input.eigenkapital`, sofern dieser bereits >= 0 war. */
  eigenkapital: number;
  finanzierungsluecke: number;
  /** Abgeleitet (`loan / kaufpreis`), ausschliesslich fuer Anzeige -- kein Eingabewert mehr. */
  ltvPct: number;
}

export function computeFinancing(input: FinancingInput): FinancingResult {
  const kaufpreis = Math.max(0, input.kaufpreis);
  const nebenkosten = Math.max(0, input.nebenkosten);
  const ekEingabe = Math.max(0, input.eigenkapital);
  const financingOn = input.financingOn ?? true;

  if (!financingOn) {
    return { loan: 0, eigenkapital: ekEingabe, finanzierungsluecke: 0, ltvPct: 0 };
  }

  const ekFuerNk = Math.min(ekEingabe, nebenkosten);
  const ekRest = ekEingabe - ekFuerNk;
  const loanAuto = Math.max(0, kaufpreis - ekRest);
  const loan = input.manualLoan ? Math.max(0, input.darlehenManual ?? 0) : loanAuto;

  const allIn = kaufpreis + nebenkosten;
  const finanzierungsluecke = Math.max(0, allIn - ekEingabe - loan);
  const ltvPct = kaufpreis > 0 ? loan / kaufpreis : 0;

  return { loan, eigenkapital: ekEingabe, finanzierungsluecke, ltvPct };
}

/** Migration (16.10.2026, einheitliche Darlehensregel): ein alter Snapshot/Export, der noch
 *  Kredit = Kaufpreis x ltvPct (EFH/Gewerbe/Gemischt) bzw. Kredit = All-in x ltvPct (ETW)
 *  speicherte, ohne eigenes Eigenkapital-Feld. Aequivalentes Eigenkapital, das unter der
 *  neuen Regel DENSELBEN Kredit ergibt, sofern der alte Kredit <= Kaufpreis war (der
 *  Normalfall bei allen 4 Typen ausser ETW mit sehr hoher Beleihung -- siehe
 *  `financingMigrationGapNote`): EK = All-in − Kredit_alt. Bei All-in x LTV mit LTV nahe
 *  100 % (nur ETW moeglich, da All-in > Kaufpreis) kann Kredit_alt > Kaufpreis sein; das
 *  neue Modell kann das nicht mehr darstellen (Kredit ist durch Kaufpreis gedeckelt) -- in
 *  diesem Fall liefert diese Funktion weiterhin EK = max(0, All-in − Kredit_alt) (kann 0
 *  sein), der migrierte Kredit wird dadurch exakt auf Kaufpreis gekappt und es entsteht eine
 *  neue, vorher nicht vorhandene Luecke = Nebenkosten. Das ist die einzig unter der neuen
 *  Regel darstellbare Annaeherung, keine beliebige Wahl (siehe DEAL-FLOW-STATUS.md). */
export function eigenkapitalFromLegacyLoan(allInAlt: number, kreditAlt: number): number {
  return Math.max(0, allInAlt - kreditAlt);
}
